/**
 * ProposalFormFields.jsx
 * Reusable input components for the Proposal flow.
 * All components use ProposalPage.css classes (pf-*).
 */

import React, { forwardRef, useEffect, useState } from "react";
import MaskedInput from "react-text-mask";
import DatePicker from "react-datepicker";
import {
  IoCalendarOutline,
  IoChevronBack,
  IoChevronForward,
} from "react-icons/io5";
import { IoMdFemale, IoMdMale } from "react-icons/io";
import "./ProposalPage.css";
import "react-datepicker/dist/react-datepicker.css";

/* -- PfField: wrapper with label + error -- */
export const PfField = ({
  label,
  required,
  error,
  children,
  fullWidth,
  className = "",
}) => (
  <div
    className={`pf-field${fullWidth ? " full-width" : ""}${
      className ? " " + className : ""
    }`}
  >
    {label && (
      <label className="pf-label">
        {label}
        {required && <span className="pf-required">*</span>}
      </label>
    )}
    {children}
    {error && <span className="pf-error">! {error}</span>}
  </div>
);

/* -- PfInput: underline text input -- */
export const PfInput = ({ label, required, error, fullWidth, ...props }) => (
  <PfField
    label={label}
    required={required}
    error={error}
    fullWidth={fullWidth}
  >
    <input
      className={`pf-input${error ? " has-error" : ""}${
        props.value ? " has-value" : ""
      }`}
      {...props}
    />
  </PfField>
);

/* -- PfMasked: masked input (passport, PAN, mobile) -- */
export const PfMasked = ({
  label,
  required,
  error,
  fullWidth,
  className = "",
  mask,
  guide = false,
  showMask = false,
  ...props
}) => (
  <PfField
    label={label}
    required={required}
    error={error}
    fullWidth={fullWidth}
    className={className}
  >
    <MaskedInput
      mask={mask}
      guide={guide}
      showMask={showMask}
      className={`pf-input${error ? " has-error" : ""}`}
      {...props}
    />
  </PfField>
);

/* -- PfSelect: underline select -- */
export const PfSelect = ({
  label,
  required,
  error,
  fullWidth,
  children,
  ...props
}) => (
  <PfField
    label={label}
    required={required}
    error={error}
    fullWidth={fullWidth}
  >
    <select className={`pf-select${error ? " has-error" : ""}`} {...props}>
      {children}
    </select>
  </PfField>
);

const toNativeDateValue = (value) => {
  if (!value) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const yyyy = value.getFullYear();
    const mm = String(value.getMonth() + 1).padStart(2, "0");
    const dd = String(value.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  const text = String(value).trim();
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text.slice(0, 10);
  if (/^\d{2}-\d{2}-\d{4}$/.test(text)) {
    const [dd, mm, yyyy] = text.split("-");
    return `${yyyy}-${mm}-${dd}`;
  }

  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return "";
  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  const dd = String(parsed.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const parseDateValue = (value) => {
  const normalized = toNativeDateValue(value);
  if (!normalized) return null;
  const parsed = new Date(`${normalized}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatTypedDate = (value) => {
  const digits = String(value || "")
    .replace(/\D/g, "")
    .slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
};

export const isAtLeastAge = (value, minYears) => {
  const parsed = parseDateValue(value);
  if (!parsed) return false;
  const now = new Date();
  const cutoff = new Date(
    now.getFullYear() - Number(minYears || 0),
    now.getMonth(),
    now.getDate(),
  );
  return parsed <= cutoff;
};

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DateInput = forwardRef(
  ({
    value,
    onClick,
    onChange,
    onBlur,
    onTypedValueCommit,
    placeholder,
    className,
    allowTyping,
  }, ref) => (
    <DateInputInner
      ref={ref}
      value={value}
      onClick={onClick}
      onChange={onChange}
      onBlur={onBlur}
      onTypedValueCommit={onTypedValueCommit}
      placeholder={placeholder}
      className={className}
      allowTyping={allowTyping}
    />
  ),
);

const DateInputInner = forwardRef(
  ({
    value,
    onClick,
    onChange,
    onBlur,
    onTypedValueCommit,
    placeholder,
    className,
    allowTyping,
  }, ref) => {
    const [draftValue, setDraftValue] = useState(value || "");

    useEffect(() => {
      setDraftValue(value || "");
    }, [value]);

    return (
      <div className="pf-date-shell">
        <input
          ref={ref}
          type="text"
          onClick={onClick}
          onChange={(e) => {
            if (!allowTyping) {
              onChange?.(e);
              return;
            }
            const next = formatTypedDate(e.target.value);
            setDraftValue(next);
            onChange?.({
              ...e,
              target: {
                ...e.target,
                value: next,
              },
            });
            if (next.length === 10) {
              onTypedValueCommit?.(next);
            }
          }}
          onBlur={(e) => {
            onBlur?.(e);
            if (allowTyping) {
              if (draftValue.length === 10) {
                onTypedValueCommit?.(draftValue);
              }
            }
          }}
          readOnly={!allowTyping}
          inputMode={allowTyping ? "numeric" : undefined}
          autoComplete="off"
          value={draftValue}
          placeholder={placeholder}
          className={className}
        />
        <button
          type="button"
          className="pf-date-icon-btn"
          onClick={onClick}
          aria-label="Open date picker"
          tabIndex={-1}
        >
          <IoCalendarOutline aria-hidden="true" />
        </button>
      </div>
    );
  },
);

/* -- PfDate: custom premium date picker -- */
export const PfDate = ({
  label,
  required,
  error,
  fullWidth,
  value,
  onChange,
  onBlur,
  minDate,
  maxDate,
  placeholder = "dd-MM-yyyy",
  dateFormat = "dd-MM-yyyy",
  allowTyping = false,
  ...props
}) => {
  const selectedDate = parseDateValue(value);
  const startYear = minDate?.getFullYear?.() ?? new Date().getFullYear() - 100;
  const endYear = maxDate?.getFullYear?.() ?? new Date().getFullYear();
  const years = [];
  for (let year = endYear; year >= startYear; year -= 1) {
    years.push(year);
  }

  const commitTypedValue = (typedValue) => {
    const next = formatTypedDate(typedValue);
    if (next.length !== 10) return;
    const parsed = parseDateValue(next);
    if (!parsed) return;
    onChange?.(toNativeDateValue(parsed));
  };

  return (
    <PfField
      label={label}
      required={required}
      error={error}
      fullWidth={fullWidth}
    >
      <div className="pf-date-wrap">
        <DatePicker
          selected={selectedDate}
          onChange={(date) => {
            const next = date ? toNativeDateValue(date) : "";
            onChange?.(next);
          }}
          onBlur={onBlur}
          minDate={minDate}
          maxDate={maxDate}
          placeholderText={placeholder}
          dateFormat={dateFormat}
          showPopperArrow={false}
          popperPlacement="bottom-start"
          calendarStartDay={1}
          renderCustomHeader={({
            date,
            changeMonth,
            changeYear,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => (
            <div className="pf-date-header">
              <div className="pf-date-header-top">
                <button
                  type="button"
                  className="pf-date-nav"
                  onClick={decreaseMonth}
                  disabled={prevMonthButtonDisabled}
                  aria-label="Previous month"
                >
                  <IoChevronBack aria-hidden="true" />
                </button>
                <div className="pf-date-current">
                  {MONTH_LABELS[date.getMonth()]} {date.getFullYear()}
                </div>
                <button
                  type="button"
                  className="pf-date-nav"
                  onClick={increaseMonth}
                  disabled={nextMonthButtonDisabled}
                  aria-label="Next month"
                >
                  <IoChevronForward aria-hidden="true" />
                </button>
              </div>

              <div className="pf-date-header-filter-row">
                <div className="pf-date-filter pf-date-filter--month">
                  {/* <span className="pf-date-filter-label">Month</span> */}
                  <select
                    value={date.getMonth()}
                    onChange={(e) => changeMonth(Number(e.target.value))}
                    aria-label="Select month"
                  >
                    {MONTH_LABELS.map((month, index) => (
                      <option value={index} key={month}>
                        {month}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="pf-date-filter pf-date-filter--year">
                  {/* <span className="pf-date-filter-label">Year</span> */}
                  <select
                    value={date.getFullYear()}
                    onChange={(e) => changeYear(Number(e.target.value))}
                    aria-label="Select year"
                  >
                    {years.map((year) => (
                      <option value={year} key={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
          className={`pf-input pf-date-input${error ? " has-error" : ""}${
            value ? " has-value" : ""
          }`}
          customInput={
            <DateInput
              placeholder={placeholder}
              allowTyping={allowTyping}
              onTypedValueCommit={commitTypedValue}
            />
          }
          wrapperClassName="pf-date-wrapper"
          popperClassName="pf-date-popper"
          {...props}
        />
      </div>
    </PfField>
  );
};

/* -- PfGender: male/female toggle buttons -- */
export const PfGender = ({
  label,
  required,
  error,
  value,
  onChange,
  fullWidth,
}) => (
  <PfField
    label={label}
    required={required}
    error={error}
    fullWidth={fullWidth}
  >
    <div className="pf-gender-group">
      <button
        type="button"
        className={`pf-gender-btn${value === "Male" ? " active" : ""}`}
        onClick={() => onChange("Male")}
        aria-pressed={value === "Male"}
      >
        <IoMdMale className="pf-gender-btn-icon" aria-hidden="true" />
        <span>Male</span>
      </button>
      <button
        type="button"
        className={`pf-gender-btn${value === "Female" ? " active" : ""}`}
        onClick={() => onChange("Female")}
        aria-pressed={value === "Female"}
      >
        <IoMdFemale className="pf-gender-btn-icon" aria-hidden="true" />
        <span>Female</span>
      </button>
    </div>
  </PfField>
);

/* -- PfSectionLabel: divider label inside grid -- */
export const PfSectionLabel = ({ children }) => (
  <div className="pg-form-section-label">{children}</div>
);

/* Mask presets for convenience */
export const MASKS = {
  name: Array(40).fill(/[a-zA-Z\s]/),
  mobile: [/[6-9]/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
  passport: [/[a-zA-Z]/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
  pan: [
    /[a-zA-Z]/,
    /[a-zA-Z]/,
    /[a-zA-Z]/,
    /[a-zA-Z]/,
    /[a-zA-Z]/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /[a-zA-Z]/,
  ],
  pincode: [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
  phone15: Array(15).fill(/\d/),
  phone10: [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
};
